
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Utensils, 
  Wine, 
  Music, 
  Hotel,
  Star,
  Sparkles
} from "lucide-react";
import { LandingPageData } from "@/models/storeModel";

interface TemplateSelectorProps {
  onSelectTemplate: (template: LandingPageData) => void;
}

const businessTypes = [
  {
    type: 'restaurant' as const,
    name: 'Restaurant',
    icon: Utensils,
    color: 'from-orange-500 to-red-500',
    description: 'Perfect for restaurants, cafes, and dining establishments',
    features: ['Menu showcase', 'Reservation system', 'Chef specials', 'Contact info']
  },
  {
    type: 'bar' as const,
    name: 'Bar & Pub',
    icon: Wine,
    color: 'from-purple-500 to-pink-500',
    description: 'Ideal for bars, pubs, and cocktail lounges',
    features: ['Drink menu', 'Happy hour deals', 'Events calendar', 'Atmosphere photos']
  },
  {
    type: 'nightclub' as const,
    name: 'Nightclub',
    icon: Music,
    color: 'from-blue-500 to-purple-500',
    description: 'Dynamic templates for nightclubs and entertainment venues',
    features: ['Event lineup', 'DJ schedules', 'VIP booking', 'Gallery showcase']
  },
  {
    type: 'hotel' as const,
    name: 'Hotel',
    icon: Hotel,
    color: 'from-green-500 to-teal-500',
    description: 'Elegant designs for hotels and accommodation',
    features: ['Room showcase', 'Booking system', 'Amenities', 'Location info']
  }
];

const templates = [
  {
    id: 'modern-restaurant',
    name: 'Modern Elegance',
    type: 'restaurant' as const,
    preview: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop',
    premium: false
  },
  {
    id: 'cozy-restaurant',
    name: 'Cozy Charm',
    type: 'restaurant' as const,
    preview: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400&h=300&fit=crop',
    premium: false
  },
  {
    id: 'urban-bar',
    name: 'Urban Vibe',
    type: 'bar' as const,
    preview: 'https://images.unsplash.com/photo-1566417109207-4717567101fb?w=400&h=300&fit=crop',
    premium: false
  },
  {
    id: 'luxury-bar',
    name: 'Luxury Lounge',
    type: 'bar' as const,
    preview: 'https://images.unsplash.com/photo-1574224342304-8b5ac6f0cbff?w=400&h=300&fit=crop',
    premium: true
  },
  {
    id: 'electric-nightclub',
    name: 'Electric Nights',
    type: 'nightclub' as const,
    preview: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop',
    premium: false
  },
  {
    id: 'vip-nightclub',
    name: 'VIP Experience',
    type: 'nightclub' as const,
    preview: 'https://images.unsplash.com/photo-1571266028243-d220c2dcb05f?w=400&h=300&fit=crop',
    premium: true
  },
  {
    id: 'boutique-hotel',
    name: 'Boutique Luxury',
    type: 'hotel' as const,
    preview: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&h=300&fit=crop',
    premium: false
  },
  {
    id: 'resort-hotel',
    name: 'Resort Paradise',
    type: 'hotel' as const,
    preview: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&h=300&fit=crop',
    premium: true
  }
];

export function TemplateSelector({ onSelectTemplate }: TemplateSelectorProps) {
  const [selectedBusinessType, setSelectedBusinessType] = React.useState<string | null>(null);

  const createTemplateData = (template: typeof templates[0]): LandingPageData => {
    const baseColors = {
      restaurant: { primary: '#f97316', secondary: '#dc2626', accent: '#facc15' },
      bar: { primary: '#a855f7', secondary: '#ec4899', accent: '#8b5cf6' },
      nightclub: { primary: '#3b82f6', secondary: '#8b5cf6', accent: '#06b6d4' },
      hotel: { primary: '#059669', secondary: '#0d9488', accent: '#10b981' }
    };

    return {
      store_id: '',
      title: `${template.name} Landing Page`,
      template_type: template.type,
      sections: [
        {
          id: 'logo',
          type: 'logo',
          content: { logoUrl: '', businessName: 'Your Business' },
          position: 0
        },
        {
          id: 'hero',
          type: 'hero',
          content: {
            title: 'Welcome to Your Business',
            subtitle: 'Experience the best in hospitality',
            backgroundImage: template.preview,
            ctaText: 'Learn More',
            ctaLink: '#about'
          },
          position: 1
        },
        {
          id: 'about',
          type: 'about',
          content: {
            title: 'About Us',
            description: 'We provide exceptional service and unforgettable experiences.',
            image: template.preview
          },
          position: 2
        },
        ...(template.type === 'restaurant' ? [{
          id: 'menu',
          type: 'menu' as const,
          content: {
            title: 'Our Menu',
            categories: [
              {
                name: 'Appetizers',
                items: [
                  { name: 'Bruschetta', description: 'Fresh tomatoes, basil, and mozzarella on toasted bread', price: '$12.00' },
                  { name: 'Calamari', description: 'Crispy fried squid with marinara sauce', price: '$14.00' }
                ]
              },
              {
                name: 'Main Courses',
                items: [
                  { name: 'Grilled Salmon', description: 'Atlantic salmon with lemon herb butter', price: '$26.00' },
                  { name: 'Ribeye Steak', description: '12oz prime cut with garlic mashed potatoes', price: '$32.00' }
                ]
              }
            ]
          },
          position: 3
        }] : []),
        ...(template.type === 'restaurant' ? [{
          id: 'reservations',
          type: 'reservations' as const,
          content: {
            title: 'Make a Reservation',
            subtitle: 'Book your table for an unforgettable dining experience',
            buttonText: 'Reserve Now',
            reservationUrl: ''
          },
          position: 4
        }] : []),
        {
          id: 'offers',
          type: 'offers',
          content: {
            title: 'Special Offers',
            offers: [
              { title: 'Special Deal 1', description: 'Amazing offer description', image: template.preview },
              { title: 'Special Deal 2', description: 'Another great offer', image: template.preview }
            ]
          },
          position: template.type === 'restaurant' ? 5 : 3
        },
        {
          id: 'footer',
          type: 'footer',
          content: {
            contactInfo: {
              address: '123 Business Street',
              phone: '+1 234 567 8900',
              email: 'info@business.com'
            },
            socialLinks: {
              facebook: '',
              instagram: '',
              twitter: ''
            }
          },
          position: template.type === 'restaurant' ? 6 : 4
        }
      ],
      published: false,
      theme_colors: baseColors[template.type]
    };
  };

  const filteredTemplates = selectedBusinessType 
    ? templates.filter(t => t.type === selectedBusinessType)
    : templates;

  return (
    <div className="space-y-8">
      {/* Business Type Selection */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Choose Your Business Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {businessTypes.map((business) => {
            const Icon = business.icon;
            const isSelected = selectedBusinessType === business.type;
            
            return (
              <Card 
                key={business.type}
                className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  isSelected 
                    ? 'ring-2 ring-blue-500 shadow-lg scale-105' 
                    : 'hover:scale-102'
                }`}
                onClick={() => setSelectedBusinessType(
                  selectedBusinessType === business.type ? null : business.type
                )}
              >
                <CardHeader className="text-center pb-2">
                  <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-r ${business.color} flex items-center justify-center mb-3`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-lg">{business.name}</CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-sm text-muted-foreground mb-3">
                    {business.description}
                  </p>
                  <div className="space-y-1">
                    {business.features.map((feature, index) => (
                      <div key={index} className="text-xs text-muted-foreground flex items-center justify-center">
                        <div className="w-1 h-1 bg-blue-500 rounded-full mr-2"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Template Selection */}
      <div>
        <h2 className="text-2xl font-bold mb-4">
          {selectedBusinessType ? 'Choose Your Template' : 'All Templates'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden">
              <div className="relative">
                <img 
                  src={template.preview} 
                  alt={template.name}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {template.premium && (
                  <Badge className="absolute top-2 right-2 bg-gradient-to-r from-yellow-500 to-orange-500">
                    <Star className="h-3 w-3 mr-1" />
                    Premium
                  </Badge>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <Button 
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    onClick={() => onSelectTemplate(createTemplateData(template))}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Use Template
                  </Button>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  {template.name}
                  <Badge variant="outline" className="text-xs">
                    {businessTypes.find(b => b.type === template.type)?.name}
                  </Badge>
                </CardTitle>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
